import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, Image, Package, FileText, Mail, Users, Settings, LogOut, 
  Save, Plus, Trash2, Edit, Eye, RefreshCw, X, BarChart3, ShoppingCart,
  UserCircle, Megaphone, ArrowUpRight, ArrowDownRight, DollarSign,
  Layers, BookOpen, FolderTree, Gift, ImageIcon, Star, MapPin, Download, Clock, Server
} from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import axios from 'axios';
import { GuideEditionsTab } from '../components/admin/GuideEditionsTab';
import { ImageLibraryPicker } from '../components/admin/ImageLibraryPicker';
import { HeroSchedulesPanel } from '../components/admin/HeroSchedulesPanel';
import { ImagesTab } from '../components/admin/ImagesTab';
import { SeoSettingsCard } from '../components/admin/SeoSettingsCard';
import { FaqEditorPanel } from '../components/admin/FaqEditorPanel';
import { AnalyticsSnapshot } from '../components/admin/AnalyticsSnapshot';
import { RichTextEditor } from '../components/admin/RichTextEditor';
import { TrafficChart, JourneyDetails, BlogPerformance } from '../components/admin/TrafficPanels';
import { CampaignLinksPanel } from '../components/admin/CampaignLinksPanel';
import { GuideLeadsTab } from '../components/admin/GuideLeadsTab';
import { RevenueGoalCard } from '../components/admin/RevenueGoalCard';
import { CountryIntrosPanel } from '../components/admin/CountryIntrosPanel';
import { StudioStatusPanel } from '../components/admin/StudioStatusPanel';
import { PracticeModePanel } from '../components/admin/PracticeModePanel';
import { ClearPracticeOrders } from '../components/admin/ClearPracticeOrders';
import { assetUrl, invalidateSiteAssets } from '../lib/siteAssets';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('analytics');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [analyticsData, setAnalyticsData] = useState(null);
  const [salesData, setSalesData] = useState(null);
  const [notifyModal, setNotifyModal] = useState(null);
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [userJourney, setUserJourney] = useState(null);
  const [contentData, setContentData] = useState(null);
  const [siteContent, setSiteContent] = useState([]);
  const [knowledgeBase, setKnowledgeBase] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [orders, setOrders] = useState([]);
  const [crmContacts, setCrmContacts] = useState([]);
  const [crmStats, setCrmStats] = useState({});
  const [blogPosts, setBlogPosts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [images, setImages] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [settings, setSettings] = useState({});
  const [editingItem, setEditingItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const navigate = useNavigate();

  const token = localStorage.getItem('admin_token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const resendLinks = async (orderId) => {
    try {
      await axios.post(`${API}/admin/orders/${orderId}/resend-links`, {}, authHeaders);
      toast.success('Fresh links issued and emailed to the buyer');
    } catch { toast.error('Could not resend links'); }
  };

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    verifyAndLoadData();
  }, [token, navigate]);

  const verifyAndLoadData = async () => {
    try {
      await axios.get(`${API}/admin/verify`, authHeaders);
      await loadData();
    } catch (err) {
      localStorage.removeItem('admin_token');
      navigate('/admin');
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, productsRes, blogRes, subsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, authHeaders),
        axios.get(`${API}/admin/products`, authHeaders),
        axios.get(`${API}/admin/blog`, authHeaders),
        axios.get(`${API}/admin/subscribers`, authHeaders),
        axios.get(`${API}/admin/contacts`, authHeaders)
      ]);
      setStats(statsRes.data);
      setProducts(productsRes.data.products);
      setBlogPosts(blogRes.data.posts);
      setSubscribers(subsRes.data.subscribers);
      
      // Initialize site content from assets
      const assetsRes = await axios.get(`${API}/site-assets`);
      setSiteContent(assetsRes.data.assets);
      
      // Load categories from backend
      await loadCategories();
      
      // Load images
      try {
        const mediaRes = await axios.get(`${API}/admin/media`, authHeaders);
        setImages(mediaRes.data.media || []);
      } catch (e) { setImages([]); }
      
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const [overview, journey, sales, content] = await Promise.all([
        axios.get(`${API}/admin/analytics/overview`, authHeaders),
        axios.get(`${API}/admin/analytics/user-journey`, authHeaders),
        axios.get(`${API}/admin/analytics/sales`, authHeaders),
        axios.get(`${API}/admin/analytics/content`, authHeaders)
      ]);
      setContentData(content.data);
      setAnalyticsData(overview.data);
      setUserJourney(journey.data);
      setSalesData(sales.data);
    } catch (err) { console.error(err); }
  };

  const sendWaitlistNotify = async () => {
    if (!notifyModal.subject || !notifyModal.message) { toast.error('Add a subject and message'); return; }
    setNotifyModal((m) => ({ ...m, sending: true }));
    try {
      const body = { country: notifyModal.country, subject: notifyModal.subject, message: notifyModal.message };
      if (notifyModal.scheduledFor) {
        const iso = new Date(notifyModal.scheduledFor).toISOString();
        if (new Date(iso) <= new Date()) { toast.error('Pick a future date and time'); setNotifyModal((m) => ({ ...m, sending: false })); return; }
        body.send_at = iso;
      }
      const res = await axios.post(`${API}/admin/waitlist/notify`, body, authHeaders);
      if (res.data.scheduled) {
        toast.success(`Scheduled for ${new Date(res.data.send_at).toLocaleString()} (${res.data.total} subscribers)`);
        loadScheduled();
      } else {
        toast.success(`Sent to ${res.data.sent} of ${res.data.total} ${notifyModal.country} subscribers`);
      }
      setNotifyModal(null);
    } catch {
      toast.error('Failed to send');
      setNotifyModal((m) => ({ ...m, sending: false }));
    }
  };

  const loadScheduled = async () => {
    try {
      const res = await axios.get(`${API}/admin/scheduled-emails`, authHeaders);
      setScheduledEmails(res.data.scheduled || []);
    } catch (err) { console.error(err); }
  };

  const cancelScheduled = async (id) => {
    try {
      await axios.delete(`${API}/admin/scheduled-emails/${id}`, authHeaders);
      toast.success('Scheduled email cancelled');
      loadScheduled();
    } catch { toast.error('Failed to cancel'); }
  };

  const loadCampaigns = async () => {
    try {
      const res = await axios.get(`${API}/admin/campaigns`, authHeaders);
      setCampaigns(res.data.campaigns);
    } catch (err) { console.error(err); }
  };

  const loadOrders = async () => {
    try {
      const res = await axios.get(`${API}/admin/orders`, authHeaders);
      setOrders(res.data.orders);
    } catch (err) { console.error(err); }
  };

  const loadCRM = async () => {
    try {
      const res = await axios.get(`${API}/admin/crm`, authHeaders);
      setCrmContacts(res.data.contacts);
      setCrmStats(res.data.stats);
    } catch (err) { console.error(err); }
  };

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings`, authHeaders);
      setSettings(res.data.settings);
    } catch (err) { console.error(err); }
  };

  const loadReviews = async () => {
    try {
      const res = await axios.get(`${API}/admin/reviews`, authHeaders);
      setReviews(res.data.reviews || []);
    } catch (err) { console.error(err); }
  };

  const loadCategories = async () => {
    try {
      const res = await axios.get(`${API}/admin/categories`, authHeaders);
      setCategories(res.data.categories || []);
    } catch (err) { console.error(err); }
  };

  const loadWaitlist = async () => {
    try {
      const res = await axios.get(`${API}/admin/waitlist`, authHeaders);
      setWaitlistEntries(res.data.entries || []);
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    if (!showModal) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !document.querySelector('[data-testid=image-library-modal]')) { setShowModal(false); setEditingItem(null); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showModal]);

  const uploadFile = async (file, kind) => {
    const form = new FormData();
    form.append('file', file);
    const endpoint = kind === 'image' ? 'upload-image' : 'upload-guide';
    const res = await axios.post(`${API}/admin/${endpoint}`, form, {
      headers: { ...authHeaders.headers, 'Content-Type': 'multipart/form-data' },
    });
    const { size, original_size: orig } = res.data;
    if (orig && size < orig) toast.info(`Image optimised for faster loading: ${(orig / 1048576).toFixed(1)} MB → ${(size / 1048576).toFixed(1)} MB`);
    return res.data;
  };

  const exportWaitlist = (country) => {
    const rows = waitlistEntries.filter((e) => !country || e.country === country);
    if (rows.length === 0) { toast.error('No entries to export'); return; }
    const header = 'Email,Country,Date\n';
    const body = rows.map((e) => `${e.email},${e.country},${e.created_at || ''}`).join('\n');
    const blob = new Blob([header + body], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `waitlist-${country || 'all'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} ${country || ''} email${rows.length > 1 ? 's' : ''}`);
  };

  useEffect(() => {
    if (activeTab === 'analytics' && !analyticsData) loadAnalytics();
    if (activeTab === 'campaigns' && campaigns.length === 0) loadCampaigns();
    if (activeTab === 'orders' && orders.length === 0) loadOrders();
    if (activeTab === 'crm' && crmContacts.length === 0) loadCRM();
    if (activeTab === 'reviews' && reviews.length === 0) loadReviews();
    if (activeTab === 'categories' && categories.length === 0) loadCategories();
    if (activeTab === 'waitlist' && waitlistEntries.length === 0) loadWaitlist();
    if (activeTab === 'waitlist') loadScheduled();
    if (activeTab === 'settings' && Object.keys(settings).length === 0) loadSettings();
  }, [activeTab]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const handleSave = async () => {
    try {
      if (modalType === 'siteContent') {
        await axios.put(`${API}/admin/site-assets/${editingItem.id}`, editingItem, authHeaders);
        invalidateSiteAssets();
        const assetsRes = await axios.get(`${API}/site-assets`);
        setSiteContent(assetsRes.data.assets);
      } else if (modalType === 'product') {
        const { isNew, ...payload } = editingItem;
        if (isNew) {
          await axios.post(`${API}/admin/products`, payload, authHeaders);
        } else {
          await axios.put(`${API}/admin/products/${editingItem.id}`, payload, authHeaders);
        }
        await loadData();
      } else if (modalType === 'bundle') {
        // Store bundles locally for now
        if (editingItem.isNew) {
          setBundles([...bundles, { ...editingItem, id: Date.now().toString() }]);
        } else {
          setBundles(bundles.map(b => b.id === editingItem.id ? editingItem : b));
        }
      } else if (modalType === 'blog') {
        await axios.put(`${API}/admin/blog/${editingItem.id}`, editingItem, authHeaders);
        await loadData();
      } else if (modalType === 'review') {
        if (editingItem.id) {
          await axios.put(`${API}/admin/reviews/${editingItem.id}`, editingItem, authHeaders);
        } else {
          await axios.post(`${API}/admin/reviews`, editingItem, authHeaders);
        }
        await loadReviews();
      } else if (modalType === 'campaign') {
        if (editingItem.id) {
          await axios.put(`${API}/admin/campaigns/${editingItem.id}`, editingItem, authHeaders);
        } else {
          await axios.post(`${API}/admin/campaigns`, editingItem, authHeaders);
        }
        await loadCampaigns();
      } else if (modalType === 'crm') {
        if (editingItem.id) {
          await axios.put(`${API}/admin/crm/${editingItem.id}`, editingItem, authHeaders);
        } else {
          await axios.post(`${API}/admin/crm`, editingItem, authHeaders);
        }
        await loadCRM();
      } else if (modalType === 'image') {
        await axios.post(`${API}/admin/media`, editingItem, authHeaders);
        const mediaRes = await axios.get(`${API}/admin/media`, authHeaders);
        setImages(mediaRes.data.media || []);
      } else if (modalType === 'knowledgeBase') {
        if (editingItem.isNew) {
          setKnowledgeBase([...knowledgeBase, { ...editingItem, id: Date.now().toString() }]);
        } else {
          setKnowledgeBase(knowledgeBase.map(k => k.id === editingItem.id ? editingItem : k));
        }
      } else if (modalType === 'category') {
        if (editingItem.id) {
          await axios.put(`${API}/admin/categories/${editingItem.id}`, editingItem, authHeaders);
        } else {
          await axios.post(`${API}/admin/categories`, editingItem, authHeaders);
        }
        await loadCategories();
      } else if (modalType === 'settings') {
        await axios.put(`${API}/admin/settings`, editingItem, authHeaders);
        setSettings(editingItem);
      }
      setShowModal(false);
      setEditingItem(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const restoreProduct = async (id) => {
    try { await axios.post(`${API}/admin/products/${id}/restore`, {}, authHeaders); toast.success("Product restored to the shop"); loadData(); }
    catch { toast.error('Could not restore product'); }
  };

  const handleDelete = async (type, id, label) => {
    if (!window.confirm(label ? `Delete "${label}"? This can't be undone.` : 'Are you sure?')) return;
    try {
      if (type === 'product') await axios.delete(`${API}/admin/products/${id}`, authHeaders);
      else if (type === 'blog') await axios.delete(`${API}/admin/blog/${id}`, authHeaders);
      else if (type === 'campaign') await axios.delete(`${API}/admin/campaigns/${id}`, authHeaders);
      else if (type === 'crm') await axios.delete(`${API}/admin/crm/${id}`, authHeaders);
      else if (type === 'image') await axios.delete(`${API}/admin/media/${id}`, authHeaders);
      else if (type === 'review') await axios.delete(`${API}/admin/reviews/${id}`, authHeaders);
      else if (type === 'category') await axios.delete(`${API}/admin/categories/${id}`, authHeaders);
      else if (type === 'bundle') setBundles(bundles.filter(b => b.id !== id));
      else if (type === 'knowledgeBase') setKnowledgeBase(knowledgeBase.filter(k => k.id !== id));
      if (type === 'blog') { setBlogPosts((list) => list.filter((p) => p.id !== id)); toast.success('Post deleted'); }

      await loadData();
      if (type === 'campaign') await loadCampaigns();
      if (type === 'crm') await loadCRM();
      if (type === 'review') await loadReviews();
      if (type === 'category') await loadCategories();
      if (type === 'image') {
        const mediaRes = await axios.get(`${API}/admin/media`, authHeaders);
        setImages(mediaRes.data.media || []);
      }
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const openModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item || getDefaultItem(type));
    setShowModal(true);
  };

  const getDefaultItem = (type) => {
    switch(type) {
      case 'campaign': return { name: '', type: 'email', status: 'draft', subject: '', content: '', audience: 'all' };
      case 'crm': return { email: '', name: '', phone: '', company: '', source: 'manual', tags: [], status: 'lead', notes: '' };
      case 'image': return { name: '', url: '', type: 'image', alt_text: '' };
      case 'blog': return { id: '', title: '', excerpt: '', content: '', category: 'Italy', date: new Date().toISOString().split('T')[0], read_time: '5 min read', image: '', status: 'draft' };
      case 'review': return { quote: '', author: '', location: '', rating: 5, status: 'published' };
      case 'product': return { isNew: true, id: '', title: '', description: '', price: 0, currency: 'AUD', category: 'Italy', featured: false, image_url: '', guide_path: '', guide_filename: '' };
      case 'category': return { name: '', description: '' };
      case 'bundle': return { isNew: true, name: '', description: '', products: [], price: 0, discount: 0 };
      case 'knowledgeBase': return { isNew: true, title: '', content: '', category: 'General' };
      case 'settings': return settings;
      default: return {};
    }
  };

  const trendProps = (pct) => (pct === null || pct === undefined ? {} : { trend: pct >= 0 ? 'up' : 'down', trendValue: `${pct >= 0 ? '+' : ''}${pct}% vs previous 30 days` });

  const BlogStatusBadge = ({ post }) => {
    const st = post.status || 'published';
    const live = st === 'published' || (st === 'scheduled' && post.publish_at_utc && new Date(post.publish_at_utc) <= new Date());
    const when = post.publish_at ? new Date(post.publish_at).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' }) : '';
    if (live) return <span data-testid={`blog-status-${post.id}`} className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-600">{st === 'scheduled' ? `Live since ${when}` : 'published'}</span>;
    if (st === 'scheduled') return <span data-testid={`blog-status-${post.id}`} className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">Scheduled · {when}</span>;
    return <span data-testid={`blog-status-${post.id}`} className="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-600">draft</span>;
  };

  const StatCard = ({ title, value, icon: Icon, color, trend, trendValue }) => (
    <div className="bg-white rounded-2xl border border-[#E6DAC5] p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-[#1E1622]/60">{title}</p>
          <p className="font-serif text-3xl font-semibold text-[#1E1622] mt-1">{value}</p>
          {trend && (
            <div className={`flex items-center gap-1 mt-2 text-sm ${trend === 'up' ? 'text-green-600' : 'text-red-500'}`}>
              {trend === 'up' ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
              <span>{trendValue}</span>
            </div>
          )}
        </div>
        <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
          <Icon size={24} className="text-white" />
        </div>
      </div>
    </div>
  );

  const sidebarItems = [
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'siteContent', label: 'Site Content', icon: Layers },
    { id: 'knowledgeBase', label: 'Knowledge Base', icon: BookOpen },
    { id: 'categories', label: 'Categories', icon: FolderTree },
    { id: 'products', label: 'Products', icon: Package },
    { id: 'bundles', label: 'Bundles', icon: Gift },
    { id: 'campaigns', label: 'Campaigns', icon: Megaphone },
    { id: 'orders', label: 'Orders', icon: ShoppingCart },
    { id: 'studios', label: 'Studio Status', icon: Server },
    { id: 'guideEditions', label: 'Guide Editions', icon: BookOpen },
    { id: 'guideLeads', label: 'Guide Leads', icon: Users },
    { id: 'crm', label: 'CRM', icon: UserCircle },
    { id: 'blog', label: 'Blog', icon: FileText },
    { id: 'reviews', label: 'Reviews', icon: Star },
    { id: 'images', label: 'Images', icon: ImageIcon },
    { id: 'newsletter', label: 'Newsletter', icon: Mail },
    { id: 'waitlist', label: 'Waitlist', icon: MapPin },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#3B164C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#1E1622]/60">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5EFE9]">
      {/* Header */}
      <div className="bg-white border-b border-[#E6DAC5] px-6 py-4 mt-20">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-serif text-2xl font-semibold text-[#1E1622]">Admin Dashboard</h1>
            <p className="text-sm text-[#1E1622]/60">Manage your site content and settings</p>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-[#3B164C] hover:underline flex items-center gap-1">
              <Eye size={16} /> View Site
            </Link>
            <button onClick={handleLogout} data-testid="admin-logout" className="flex items-center gap-2 text-sm text-red-500 hover:text-red-600">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <div className="w-52 flex-shrink-0">
            <div className="bg-white rounded-2xl border border-[#E6DAC5] p-3 sticky top-28">
              <nav className="space-y-1">
                {sidebarItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    data-testid={`admin-nav-${item.id}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-sm transition-all ${
                      activeTab === item.id ? 'bg-[#3B164C] text-white' : 'text-[#1E1622]/70 hover:bg-[#F5EFE9]'
                    }`}
                  >
                    <item.icon size={18} />
                    <span className="font-medium">{item.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Analytics */}
            {activeTab === 'analytics' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <AnalyticsSnapshot authHeaders={authHeaders} />
                <RevenueGoalCard authHeaders={authHeaders} />
                <div className="flex items-center justify-between">
                  <h2 className="font-serif text-xl font-semibold text-[#1E1622]">Analytics</h2>
                  <button onClick={loadAnalytics} className="flex items-center gap-2 text-sm text-[#3B164C]">
                    <RefreshCw size={16} /> Refresh
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard title="Visitors (30 days)" value={analyticsData?.summary?.total_visitors?.toLocaleString() || '0'} icon={Users} color="bg-[#3B164C]" {...trendProps(analyticsData?.summary?.visitors_change)} />
                  <StatCard title="Page Views (30 days)" value={analyticsData?.summary?.total_page_views?.toLocaleString() || '0'} icon={Eye} color="bg-[#C5A059]" {...trendProps(analyticsData?.summary?.page_views_change)} />
                  <StatCard title="Subscribers" value={stats.subscribers || 0} icon={Mail} color="bg-[#C5A059]" />
                  <StatCard title="Revenue" value={`$${salesData?.total_revenue ?? 0}`} icon={DollarSign} color="bg-[#1E1622]" />
                </div>

                {analyticsData && <TrafficChart data={analyticsData.traffic_data} />}

                {/* Real Sales Data */}
                {salesData && (
                  <div className="bg-white rounded-2xl border border-[#C5A059]/40 p-6" data-testid="sales-panel">
                    <div className="flex items-center justify-between mb-5">
                      <h3 className="font-serif text-lg font-semibold text-[#1E1622]">Sales Overview</h3>
                      <span className="text-xs uppercase tracking-[0.2em] text-[#C5A059]">Real order data</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                      <div className="rounded-xl bg-[#F5EFE9] p-4" data-testid="sales-total-revenue">
                        <p className="text-xs text-[#1E1622]/50 uppercase tracking-wide">Total Revenue</p>
                        <p className="font-display text-3xl text-[#3B164C] mt-1">${salesData.total_revenue} <span className="text-sm text-[#C5A059]">AUD</span></p>
                      </div>
                      <div className="rounded-xl bg-[#F5EFE9] p-4" data-testid="sales-total-orders">
                        <p className="text-xs text-[#1E1622]/50 uppercase tracking-wide">Paid Orders</p>
                        <p className="font-display text-3xl text-[#3B164C] mt-1">{salesData.total_orders}</p>
                      </div>
                      <div className="rounded-xl bg-[#F5EFE9] p-4">
                        <p className="text-xs text-[#1E1622]/50 uppercase tracking-wide">Avg Order Value</p>
                        <p className="font-display text-3xl text-[#3B164C] mt-1">${salesData.avg_order_value}</p>
                      </div>
                    </div>
                    <h4 className="font-semibold text-[#1E1622] mb-3 text-sm">Top-Selling Guides</h4>
                    {salesData.top_products?.length ? (
                      <div className="space-y-2">
                        {salesData.top_products.map((p, i) => (
                          <div key={i} className="flex items-center justify-between py-2 border-b border-[#F5EFE9] last:border-0">
                            <span className="text-sm text-[#1E1622] flex items-center gap-2"><span className="text-[#C5A059] font-semibold">{i + 1}.</span> {p.title}</span>
                            <span className="text-sm text-[#1E1622]/60">{p.count} sold · <span className="text-[#3B164C] font-medium">${p.revenue}</span></span>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-sm text-[#1E1622]/50">No sales yet.</p>}
                    {salesData.monthly_revenue && (
                      <div className="mt-6" data-testid="revenue-chart">
                        <h4 className="font-semibold text-[#1E1622] mb-3 text-sm">Monthly Revenue (last 12 months)</h4>
                        <ResponsiveContainer width="100%" height={220}>
                          <LineChart data={salesData.monthly_revenue} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E6DAC5" />
                            <XAxis dataKey="label" stroke="#1E1622" fontSize={12} />
                            <YAxis stroke="#1E1622" fontSize={12} />
                            <Tooltip formatter={(v) => [`$${v}`, 'Revenue']} />
                            <Line type="monotone" dataKey="revenue" stroke="#3B164C" strokeWidth={2.5} dot={{ fill: '#C5A059', r: 3 }} activeDot={{ r: 5 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                )}

                {analyticsData && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white rounded-2xl border border-[#E6DAC5] p-6">
                      <h3 className="font-serif text-lg font-semibold text-[#1E1622] mb-4">Top Pages</h3>
                      <div className="space-y-3">
                        {analyticsData.top_pages?.map((page, i) => (
                          <div key={i} className="flex items-center justify-between py-2">
                            <span className="text-sm text-[#1E1622]">{page.page}</span>
                            <div className="flex items-center gap-4">
                              <span className="text-sm text-[#1E1622]/60">{page.views} views</span>
                              <span className="text-xs text-[#3B164C]">{page.avg_time}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="bg-white rounded-2xl border border-[#E6DAC5] p-6">
                      <h3 className="font-serif text-lg font-semibold text-[#1E1622] mb-4">Traffic Sources</h3>
                      <div className="space-y-3">
                        {analyticsData.traffic_sources?.map((source, i) => (
                          <div key={i} className="flex items-center justify-between py-2">
                            <span className="text-sm text-[#1E1622]">{source.source}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-24 h-2 bg-[#F5EFE9] rounded-full overflow-hidden">
                                <div className="h-full bg-[#3B164C] rounded-full" style={{ width: `${source.percentage}%` }}></div>
                              </div>
                              <span className="text-xs text-[#1E1622]/60 w-8">{source.percentage}%</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {userJourney && (
                  <div className="bg-white rounded-2xl border border-[#E6DAC5] p-6">
                    <h3 className="font-serif text-lg font-semibold text-[#1E1622] mb-1">Visitor Journey · last 30 days</h3>
                    <p className="text-sm text-[#1E1622]/55 mb-4" data-testid="journey-summary">{userJourney.sessions} visits · {analyticsData?.summary?.conversion_rate ?? 0}% ended in a purchase</p>
                    <div className="flex items-end justify-between gap-4 h-48">
                      {userJourney.funnel?.map((step, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center">
                          <div className="w-full bg-[#3B164C] rounded-t-lg" style={{ height: `${Math.max(step.percentage * 1.5, 2)}px` }}></div>
                          <p className="text-xs text-[#1E1622]/60 mt-2 text-center">{step.step}</p>
                          <p className="text-sm font-semibold text-[#1E1622]">{step.users}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {userJourney && <JourneyDetails journey={userJourney} />}
                {contentData && <BlogPerformance content={contentData} />}
              </motion.div>
            )}

            {/* Site Content */}
            {activeTab === 'siteContent' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <h2 className="font-serif text-xl font-semibold text-[#1E1622]">Site Content</h2>
                <HeroSchedulesPanel token={token} />
                <CountryIntrosPanel authHeaders={authHeaders} />
                <FaqEditorPanel authHeaders={authHeaders} />
                <div className="grid gap-4">
                  {siteContent.map((item) => (
                    <div key={item.id} className="bg-white rounded-2xl border border-[#E6DAC5] p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              item.type === 'image' ? 'bg-blue-100 text-blue-600' :
                              item.type === 'color' ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-600'
                            }`}>{item.type}</span>
                            <h3 className="font-semibold text-[#1E1622]">{item.name}</h3>
                          </div>
                          <p className="text-sm text-[#1E1622]/60 mb-3">{item.description}</p>
                          {item.type === 'image' && <img src={assetUrl(item.value)} alt={item.name} className="h-16 w-auto rounded-lg object-cover" data-testid={`site-content-thumb-${item.id}`} />}
                          {item.type === 'color' && (
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg border" style={{ backgroundColor: item.value }}></div>
                              <span className="text-sm font-mono">{item.value}</span>
                            </div>
                          )}
                          {item.type === 'text' && <p className="text-sm text-[#1E1622] bg-[#F5EFE9] p-3 rounded-lg">{item.value}</p>}
                        </div>
                        <button onClick={() => openModal('siteContent', item)} data-testid={`site-content-edit-${item.id}`} className="p-2 text-[#3B164C] hover:bg-[#3B164C]/10 rounded-lg">
                          <Edit size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Knowledge Base */}
            {activeTab === 'knowledgeBase' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="font-serif text-xl font-semibold text-[#1E1622]">Knowledge Base</h2>
                  <button onClick={() => openModal('knowledgeBase')} className="btn-primary flex items-center gap-2 text-sm">
                    <Plus size={16} /> Add Article
                  </button>
                </div>
                <div className="grid gap-4">
                  {knowledgeBase.map((article) => (
                    <div key={article.id} className="bg-white rounded-2xl border border-[#E6DAC5] p-6">
                      <div className="flex items-start justify-between">
                        <div>
                          <span className="text-xs px-2 py-1 rounded-full bg-[#3B164C]/10 text-[#3B164C]">{article.category}</span>
                          <h3 className="font-semibold text-[#1E1622] mt-2">{article.title}</h3>
                          <p className="text-sm text-[#1E1622]/60 mt-1 line-clamp-2">{article.content}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => openModal('knowledgeBase', article)} className="p-2 text-[#3B164C] hover:bg-[#3B164C]/10 rounded-lg">
                            <Edit size={16} />
                          </button>
                          <button onClick={() => handleDelete('knowledgeBase', article.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {knowledgeBase.length === 0 && (
                    <div className="bg-white rounded-2xl border border-[#E6DAC5] p-12 text-center">
                      <BookOpen size={48} className="mx-auto text-[#1E1622]/20 mb-4" />
                      <p className="text-[#1E1622]/50">No knowledge base articles yet</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Categories */}
            {activeTab === 'categories' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="categories-section">
                <div className="flex items-center justify-between">
                  <h2 className="font-serif text-xl font-semibold text-[#1E1622]">Categories</h2>
                  <button onClick={() => openModal('category')} data-testid="add-category-btn" className="btn-primary flex items-center gap-2 text-sm">
                    <Plus size={16} /> Add Category
                  </button>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categories.map((category) => (
                    <div key={category.id} data-testid={`category-row-${category.id}`} className="bg-white rounded-2xl border border-[#E6DAC5] p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-xl bg-[#3B164C]/10 flex items-center justify-center flex-shrink-0">
                            <FolderTree size={20} className="text-[#3B164C]" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-[#1E1622]">{category.name}</h3>
                            <p className="text-sm text-[#1E1622]/60">{category.count || 0} products</p>
                            {category.description && <p className="text-xs text-[#1E1622]/40 mt-1">{category.description}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => openModal('category', category)} data-testid={`edit-category-${category.id}`} className="p-2 text-[#3B164C] hover:bg-[#3B164C]/10 rounded-lg"><Edit size={16} /></button>
                          <button onClick={() => handleDelete('category', category.id)} data-testid={`delete-category-${category.id}`} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 size={16} /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {categories.length === 0 && (
                    <div className="col-span-full bg-white rounded-2xl border border-[#E6DAC5] p-12 text-center">
                      <p className="text-[#1E1622]/50">No categories yet. Add your first category.</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Products */}
            {activeTab === 'products' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="font-serif text-xl font-semibold text-[#1E1622]">Products</h2>
                  <button onClick={() => openModal('product')} data-testid="add-product-btn" className="btn-primary flex items-center gap-2 text-sm">
                    <Plus size={16} /> Add Product
                  </button>
                </div>
                <div className="grid gap-4">
                  {products.map((product) => (
                    <div key={product.id} data-testid={`admin-product-${product.id}`} className={`bg-white rounded-2xl border border-[#E6DAC5] p-6 ${product.removed ? 'opacity-60' : ''}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            {product.removed && <span data-testid={`product-removed-badge-${product.id}`} className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700">Removed from shop</span>}
                            {product.featured && <span className="text-xs px-2 py-1 rounded-full bg-[#C5A059]/20 text-[#C5A059]">Featured</span>}
                            <span className="text-xs px-2 py-1 rounded-full bg-[#C5A059]/20 text-[#C5A059]">{product.category}</span>
                          </div>
                          <h3 className="font-serif text-lg font-semibold text-[#1E1622] mb-1">{product.title}</h3>
                          <p className="text-sm text-[#1E1622]/60 mb-2">{product.description}</p>
                          <p className="text-lg font-semibold text-[#3B164C]">${product.price} {product.currency}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => openModal('product', product)} data-testid={`edit-product-${product.id}`} className="p-2 text-[#3B164C] hover:bg-[#3B164C]/10 rounded-lg">
                            <Edit size={16} />
                          </button>
                          {product.removed ? (
                            <button onClick={() => restoreProduct(product.id)} data-testid={`restore-product-${product.id}`} className="px-3 py-1.5 text-sm text-[#3B164C] border border-[#3B164C]/30 hover:border-[#C5A059] hover:text-[#C5A059] rounded-full">Restore</button>
                          ) : (
                            <button onClick={() => handleDelete('product', product.id)} data-testid={`delete-product-${product.id}`} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Bundles */}
            {activeTab === 'bundles' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="font-serif text-xl font-semibold text-[#1E1622]">Bundles</h2>
                  <button onClick={() => openModal('bundle')} className="btn-primary flex items-center gap-2 text-sm">
                    <Plus size={16} /> Create Bundle
                  </button>
                </div>
                <div className="grid gap-4">
                  {bundles.map((bundle) => (
                    <div key={bundle.id} className="bg-white rounded-2xl border border-[#E6DAC5] p-6">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-[#1E1622]">{bundle.name}</h3>
                          <p className="text-sm text-[#1E1622]/60 mt-1">{bundle.description}</p>
                          <div className="flex items-center gap-4 mt-3">
                            <span className="text-lg font-semibold text-[#3B164C]">${bundle.price} AUD</span>
                            {bundle.discount > 0 && <span className="text-sm text-green-600">Save {bundle.discount}%</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => openModal('bundle', bundle)} className="p-2 text-[#3B164C] hover:bg-[#3B164C]/10 rounded-lg">
                            <Edit size={16} />
                          </button>
                          <button onClick={() => handleDelete('bundle', bundle.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {bundles.length === 0 && (
                    <div className="bg-white rounded-2xl border border-[#E6DAC5] p-12 text-center">
                      <Gift size={48} className="mx-auto text-[#1E1622]/20 mb-4" />
                      <p className="text-[#1E1622]/50">No bundles created yet</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Campaigns */}
            {activeTab === 'campaigns' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <CampaignLinksPanel authHeaders={authHeaders} />
                <div className="flex items-center justify-between">
                  <h2 className="font-serif text-xl font-semibold text-[#1E1622]">Campaigns</h2>
                  <button onClick={() => openModal('campaign')} className="btn-primary flex items-center gap-2 text-sm">
                    <Plus size={16} /> New Campaign
                  </button>
                </div>
                <div className="grid gap-4">
                  {campaigns.map((campaign) => (
                    <div key={campaign.id} className="bg-white rounded-2xl border border-[#E6DAC5] p-6">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              campaign.status === 'sent' ? 'bg-green-100 text-green-600' :
                              campaign.status === 'scheduled' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                            }`}>{campaign.status}</span>
                            <span className="text-xs text-[#1E1622]/50">{campaign.type}</span>
                          </div>
                          <h3 className="font-semibold text-[#1E1622]">{campaign.name}</h3>
                          {campaign.subject && <p className="text-sm text-[#1E1622]/60 mt-1">{campaign.subject}</p>}
                          {campaign.sent_count > 0 && (
                            <div className="flex items-center gap-4 mt-3 text-xs text-[#1E1622]/60">
                              <span>Sent: {campaign.sent_count}</span>
                              <span>Open Rate: {campaign.open_rate}%</span>
                              <span>Click Rate: {campaign.click_rate}%</span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => openModal('campaign', campaign)} className="p-2 text-[#3B164C] hover:bg-[#3B164C]/10 rounded-lg">
                            <Edit size={16} />
                          </button>
                          <button onClick={() => handleDelete('campaign', campaign.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {campaigns.length === 0 && (
                    <div className="bg-white rounded-2xl border border-[#E6DAC5] p-12 text-center">
                      <Megaphone size={48} className="mx-auto text-[#1E1622]/20 mb-4" />
                      <p className="text-[#1E1622]/50">No campaigns yet</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Orders */}
            {activeTab === 'orders' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="font-serif text-xl font-semibold text-[#1E1622]">Orders</h2>
                  <div className="flex items-center gap-5">
                  <ClearPracticeOrders authHeaders={authHeaders} count={orders.filter((o) => o.practice).length} onDone={loadOrders} />
                  <button onClick={loadOrders} className="flex items-center gap-2 text-sm text-[#3B164C]">
                    <RefreshCw size={16} /> Refresh
                  </button>
                  </div>
                </div>
                <div className="bg-white rounded-2xl border border-[#E6DAC5] overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-[#F5EFE9]">
                      <tr>
                        <th className="text-left p-4 text-sm font-semibold text-[#1E1622]">Customer</th>
                        <th className="text-left p-4 text-sm font-semibold text-[#1E1622]">Product</th>
                        <th className="text-left p-4 text-sm font-semibold text-[#1E1622]">Amount</th>
                        <th className="text-left p-4 text-sm font-semibold text-[#1E1622]">Status</th>
                        <th className="text-left p-4 text-sm font-semibold text-[#1E1622]">Date</th>
                        <th className="text-left p-4 text-sm font-semibold text-[#1E1622]">Links</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order, i) => (
                        <tr key={i} className="border-t border-[#E6DAC5]">
                          <td className="p-4">
                            <p className="text-sm text-[#1E1622]">{order.customer_name}</p>
                            <p className="text-xs text-[#1E1622]/50">{order.customer_email}</p>
                          </td>
                          <td className="p-4 text-sm text-[#1E1622]">{order.product_title}</td>
                          <td className="p-4 text-sm font-semibold text-[#3B164C]">${order.amount} {order.currency}</td>
                          <td className="p-4">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              order.status === 'completed' ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'
                            }`}>{order.status}</span>
                            {order.practice && <span className="ml-1 text-xs px-2 py-1 rounded-full bg-[#C5A059]/20 text-[#8A6A2E]" data-testid={`order-practice-badge-${order.id}`}>Practice</span>}
                          </td>
                          <td className="p-4 text-sm text-[#1E1622]/50">{new Date(order.created_at).toLocaleDateString()}</td>
                          <td className="p-4">
                            {order.order_type === 'studio' && order.status === 'completed' && (
                              <button onClick={() => resendLinks(order.id)} data-testid={`resend-links-${order.id}`} className="text-xs text-[#3B164C] hover:text-[#C5A059] inline-flex items-center gap-1"><RefreshCw size={12} /> Resend links</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {orders.length === 0 && <p className="p-12 text-center text-[#1E1622]/50">No orders yet</p>}
                </div>
              </motion.div>
            )}

            {activeTab === 'studios' && <StudioStatusPanel authHeaders={authHeaders} />}
            {activeTab === 'guideEditions' && <GuideEditionsTab authHeaders={authHeaders} />}
            {activeTab === 'guideLeads' && <GuideLeadsTab authHeaders={authHeaders} />}

            {/* CRM */}
            {activeTab === 'crm' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="font-serif text-xl font-semibold text-[#1E1622]">CRM</h2>
                  <button onClick={() => openModal('crm')} className="btn-primary flex items-center gap-2 text-sm">
                    <Plus size={16} /> Add Contact
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <StatCard title="Total Contacts" value={crmStats.total || 0} icon={Users} color="bg-[#3B164C]" />
                  <StatCard title="Leads" value={crmStats.leads || 0} icon={UserCircle} color="bg-[#C5A059]" />
                  <StatCard title="Customers" value={crmStats.customers || 0} icon={ShoppingCart} color="bg-[#C5A059]" />
                </div>
                <div className="bg-white rounded-2xl border border-[#E6DAC5] overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-[#F5EFE9]">
                      <tr>
                        <th className="text-left p-4 text-sm font-semibold text-[#1E1622]">Contact</th>
                        <th className="text-left p-4 text-sm font-semibold text-[#1E1622]">Source</th>
                        <th className="text-left p-4 text-sm font-semibold text-[#1E1622]">Status</th>
                        <th className="text-left p-4 text-sm font-semibold text-[#1E1622]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {crmContacts.map((contact, i) => (
                        <tr key={i} className="border-t border-[#E6DAC5]">
                          <td className="p-4">
                            <p className="text-sm font-medium text-[#1E1622]">{contact.name || 'No name'}</p>
                            <p className="text-xs text-[#1E1622]/50">{contact.email}</p>
                          </td>
                          <td className="p-4 text-sm text-[#1E1622]/60">{contact.source}</td>
                          <td className="p-4">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              contact.status === 'customer' ? 'bg-green-100 text-green-600' : 'bg-[#3B164C]/10 text-[#3B164C]'
                            }`}>{contact.status}</span>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-1">
                              <button onClick={() => openModal('crm', contact)} className="p-1.5 text-[#3B164C] hover:bg-[#3B164C]/10 rounded">
                                <Edit size={14} />
                              </button>
                              <button onClick={() => handleDelete('crm', contact.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded">
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {crmContacts.length === 0 && <p className="p-12 text-center text-[#1E1622]/50">No contacts yet</p>}
                </div>
              </motion.div>
            )}

            {/* Blog */}
            {activeTab === 'blog' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="font-serif text-xl font-semibold text-[#1E1622]">Blog Posts</h2>
                  <button onClick={() => openModal('blog')} className="btn-primary flex items-center gap-2 text-sm">
                    <Plus size={16} /> New Post
                  </button>
                </div>
                <div className="grid gap-4">
                  {blogPosts.map((post) => (
                    <div key={post.id} data-testid={`admin-blog-post-${post.id}`} className="bg-white rounded-2xl border border-[#E6DAC5] p-6">
                      <div className="flex items-start gap-4">
                        <img src={post.image} alt={post.title} className="w-24 h-24 rounded-xl object-cover" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <BlogStatusBadge post={post} />
                            <span className="text-xs text-[#3B164C]">{post.category}</span>
                          </div>
                          <h3 className="font-serif text-lg font-semibold text-[#1E1622] mb-1">{post.title}</h3>
                          <p className="text-sm text-[#1E1622]/60 line-clamp-2">{post.excerpt}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => openModal('blog', post)} data-testid={`edit-blog-${post.id}`} className="p-2 text-[#3B164C] hover:bg-[#3B164C]/10 rounded-lg">
                            <Edit size={16} />
                          </button>
                          <button onClick={() => handleDelete('blog', post.id, post.title)} data-testid={`delete-blog-${post.id}`} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Reviews */}
            {activeTab === 'reviews' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="reviews-section">
                <div className="flex items-center justify-between">
                  <h2 className="font-serif text-xl font-semibold text-[#1E1622]">Customer Reviews</h2>
                  <button onClick={() => openModal('review')} data-testid="add-review-btn" className="btn-primary flex items-center gap-2 text-sm">
                    <Plus size={16} /> Add Review
                  </button>
                </div>
                <p className="text-sm text-[#1E1622]/60">These reviews appear live in the testimonials section on the homepage.</p>
                <div className="grid gap-4">
                  {reviews.map((review) => (
                    <div key={review.id} data-testid={`review-row-${review.id}`} className="bg-white rounded-2xl border border-[#E6DAC5] p-6">
                      <div className="flex items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`text-xs px-2 py-1 rounded-full ${review.status === 'published' ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'}`}>{review.status || 'published'}</span>
                            <span className="text-xs text-[#C5A059]">{'★'.repeat(review.rating || 5)}</span>
                          </div>
                          <p className="text-[#1E1622]/80 italic mb-2">"{review.quote}"</p>
                          <p className="text-sm font-semibold text-[#1E1622]">{review.author} <span className="font-normal text-[#1E1622]/50">· {review.location}</span></p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => openModal('review', review)} data-testid={`edit-review-${review.id}`} className="p-2 text-[#3B164C] hover:bg-[#3B164C]/10 rounded-lg"><Edit size={16} /></button>
                          <button onClick={() => handleDelete('review', review.id)} data-testid={`delete-review-${review.id}`} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 size={16} /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {reviews.length === 0 && <p className="text-[#1E1622]/50 text-sm">No reviews yet. Add your first customer review.</p>}
                </div>
              </motion.div>
            )}

            {/* Images */}
            {activeTab === 'images' && <ImagesTab token={token} onAddLink={() => openModal('image')} refreshKey={images} />}

            {/* Newsletter */}
            {activeTab === 'newsletter' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <h2 className="font-serif text-xl font-semibold text-[#1E1622]">Newsletter</h2>
                <div className="grid grid-cols-2 gap-4">
                  <StatCard title="Total Subscribers" value={subscribers.length} icon={Mail} color="bg-[#3B164C]" />
                  <StatCard title="Active" value={subscribers.filter(s => s.status === 'active').length} icon={Users} color="bg-[#C5A059]" />
                </div>
                <div className="bg-white rounded-2xl border border-[#E6DAC5] p-6">
                  <h3 className="font-serif text-lg font-semibold text-[#1E1622] mb-4">Subscribers</h3>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#E6DAC5]">
                        <th className="text-left py-3 text-sm font-semibold text-[#1E1622]">Email</th>
                        <th className="text-left py-3 text-sm font-semibold text-[#1E1622]">Name</th>
                        <th className="text-left py-3 text-sm font-semibold text-[#1E1622]">Status</th>
                        <th className="text-left py-3 text-sm font-semibold text-[#1E1622]">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscribers.map((sub, i) => (
                        <tr key={i} className="border-b border-[#E6DAC5] last:border-0">
                          <td className="py-3 text-sm text-[#1E1622]">{sub.email}</td>
                          <td className="py-3 text-sm text-[#1E1622]/60">{sub.name || '-'}</td>
                          <td className="py-3">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              sub.status === 'active' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'
                            }`}>{sub.status || 'active'}</span>
                          </td>
                          <td className="py-3 text-sm text-[#1E1622]/50">{new Date(sub.subscribed_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {subscribers.length === 0 && <p className="py-8 text-center text-[#1E1622]/50">No subscribers yet</p>}
                </div>
              </motion.div>
            )}

            {/* Waitlist */}
            {activeTab === 'waitlist' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="waitlist-section">
                <div className="flex items-center justify-between">
                  <h2 className="font-serif text-xl font-semibold text-[#1E1622]">Waitlist</h2>
                  <button onClick={() => exportWaitlist(null)} data-testid="export-all-waitlist-btn" className="btn-primary flex items-center gap-2 text-sm">
                    <Download size={16} /> Export All
                  </button>
                </div>
                <p className="text-sm text-[#1E1622]/60">Emails collected from the homepage "Join Waitlist" buttons. Export a country's list to message them when a kit launches.</p>
                {scheduledEmails.filter((s) => s.status === 'scheduled').length > 0 && (
                  <div className="bg-[#F5EFE9] rounded-2xl border border-[#C5A059]/40 p-6" data-testid="scheduled-emails-panel">
                    <h3 className="font-semibold text-[#1E1622] mb-3 text-sm flex items-center gap-2"><Clock size={16} className="text-[#C5A059]" /> Scheduled Launch Emails</h3>
                    <div className="space-y-2">
                      {scheduledEmails.filter((s) => s.status === 'scheduled').map((s) => (
                        <div key={s.id} data-testid={`scheduled-${s.id}`} className="flex items-center justify-between bg-white rounded-xl p-3 border border-[#E6DAC5]">
                          <div>
                            <p className="text-sm font-medium text-[#1E1622]">{s.country} · {s.subject}</p>
                            <p className="text-xs text-[#1E1622]/50">Sends {new Date(s.send_at).toLocaleString()} · {s.recipients} recipients</p>
                          </div>
                          <button onClick={() => cancelScheduled(s.id)} data-testid={`cancel-scheduled-${s.id}`} className="text-xs text-red-500 hover:bg-red-50 px-3 py-1.5 rounded-lg">Cancel</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {[...new Set(waitlistEntries.map((e) => e.country))].sort().map((country) => {
                  const rows = waitlistEntries.filter((e) => e.country === country);
                  return (
                    <div key={country} data-testid={`waitlist-country-${country.toLowerCase()}`} className="bg-white rounded-2xl border border-[#E6DAC5] p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-[#1E1622]">{country} <span className="text-sm font-normal text-[#1E1622]/50">· {rows.length} {rows.length === 1 ? 'email' : 'emails'}</span></h3>
                        <div className="flex items-center gap-2">
                          <button onClick={() => setNotifyModal({ country, subject: '', message: '', sending: false })} data-testid={`notify-waitlist-${country.toLowerCase()}`} className="btn-primary flex items-center gap-2 text-xs py-2 px-4">
                            <Mail size={14} /> Notify {country}
                          </button>
                          <button onClick={() => exportWaitlist(country)} data-testid={`export-waitlist-${country.toLowerCase()}`} className="btn-secondary flex items-center gap-2 text-xs py-2 px-4">
                            <Download size={14} /> Export {country}
                          </button>
                        </div>
                      </div>
                      <div className="space-y-1">
                        {rows.map((e, i) => (
                          <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-[#F5EFE9] last:border-0">
                            <span className="text-[#1E1622]">{e.email}</span>
                            <span className="text-[#1E1622]/40 text-xs">{(e.created_at || '').split('T')[0]}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {waitlistEntries.length === 0 && <p className="text-[#1E1622]/50 text-sm">No waitlist sign-ups yet.</p>}
              </motion.div>
            )}

            {/* Settings */}
            {activeTab === 'settings' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <h2 className="font-serif text-xl font-semibold text-[#1E1622]">Settings</h2>
                <PracticeModePanel authHeaders={authHeaders} />
                <div className="bg-white rounded-2xl border border-[#E6DAC5] p-6">
                  <div className="grid gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[#1E1622] mb-2">Site Name</label>
                      <input type="text" value={settings.site_name || ''} onChange={(e) => setSettings({...settings, site_name: e.target.value})} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1E1622] mb-2">Tagline</label>
                      <input type="text" value={settings.tagline || ''} onChange={(e) => setSettings({...settings, tagline: e.target.value})} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1E1622] mb-2">Contact Email</label>
                      <input type="email" value={settings.contact_email || ''} onChange={(e) => setSettings({...settings, contact_email: e.target.value})} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[#1E1622] mb-2">Instagram</label>
                        <input type="text" value={settings.social_instagram || ''} onChange={(e) => setSettings({...settings, social_instagram: e.target.value})} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#1E1622] mb-2">Facebook</label>
                        <input type="text" value={settings.social_facebook || ''} onChange={(e) => setSettings({...settings, social_facebook: e.target.value})} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[#1E1622] mb-2">TikTok</label>
                        <input type="text" value={settings.social_tiktok || ''} onChange={(e) => setSettings({...settings, social_tiktok: e.target.value})} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#1E1622] mb-2">Pinterest</label>
                        <input type="text" value={settings.social_pinterest || ''} onChange={(e) => setSettings({...settings, social_pinterest: e.target.value})} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" />
                      </div>
                    </div>
                    <button onClick={async () => { await axios.put(`${API}/admin/settings`, settings, authHeaders); toast.success('Settings saved!'); }} className="btn-primary flex items-center justify-center gap-2">
                      <Save size={18} /> Save Settings
                    </button>
                  </div>
                </div>
                <SeoSettingsCard authHeaders={authHeaders} />
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Waitlist Notify Modal */}
      {notifyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl w-full max-w-lg" data-testid="notify-modal">
            <div className="p-6 border-b border-[#E6DAC5] flex items-center justify-between">
              <h3 className="font-serif text-xl font-semibold text-[#1E1622]">Email the {notifyModal.country} Waitlist</h3>
              <button onClick={() => setNotifyModal(null)} className="p-2 hover:bg-[#F5EFE9] rounded-lg"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-[#1E1622]/60">Sends a one-off email to everyone on the {notifyModal.country} waitlist — perfect for launch day.</p>
              <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Subject</label>
                <input type="text" value={notifyModal.subject} onChange={(e) => setNotifyModal({ ...notifyModal, subject: e.target.value })} data-testid="notify-subject-input" placeholder={`The ${notifyModal.country} kit is live!`} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" />
              </div>
              <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Message</label>
                <textarea value={notifyModal.message} onChange={(e) => setNotifyModal({ ...notifyModal, message: e.target.value })} rows={6} data-testid="notify-message-input" placeholder="Great news — our new relocation kit just launched..." className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" />
              </div>
              <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Schedule for later (optional)</label>
                <input type="datetime-local" value={notifyModal.scheduledFor || ''} onChange={(e) => setNotifyModal({ ...notifyModal, scheduledFor: e.target.value })} data-testid="notify-schedule-input" className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" />
                <p className="text-xs text-[#1E1622]/50 mt-1">Leave empty to send immediately.</p>
              </div>
            </div>
            <div className="p-6 border-t border-[#E6DAC5] flex items-center justify-end gap-4">
              <button onClick={() => setNotifyModal(null)} className="text-[#1E1622]/60 hover:text-[#1E1622]">Cancel</button>
              <button onClick={sendWaitlistNotify} disabled={notifyModal.sending} data-testid="notify-send-btn" className="btn-primary flex items-center gap-2 disabled:opacity-60">
                <Mail size={18} /> {notifyModal.sending ? 'Sending...' : (notifyModal.scheduledFor ? 'Schedule Email' : 'Send Email')}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Modal */}
      {showModal && editingItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className={`bg-white rounded-2xl w-full ${modalType === 'blog' ? 'max-w-4xl' : 'max-w-lg'} max-h-[90vh] overflow-y-auto`}>
            <div className="p-6 border-b border-[#E6DAC5] flex items-center justify-between">
              <h3 className="font-serif text-xl font-semibold text-[#1E1622]">
                {modalType === 'siteContent' ? 'Edit Content' : modalType === 'product' ? (editingItem.isNew ? 'New Product' : 'Edit Product') : modalType === 'blog' ? (editingItem.id ? 'Edit Post' : 'New Post') : modalType === 'campaign' ? (editingItem.id ? 'Edit Campaign' : 'New Campaign') : modalType === 'crm' ? (editingItem.id ? 'Edit Contact' : 'Add Contact') : modalType === 'image' ? 'Add Image' : modalType === 'bundle' ? (editingItem.isNew ? 'Create Bundle' : 'Edit Bundle') : modalType === 'knowledgeBase' ? (editingItem.isNew ? 'Add Article' : 'Edit Article') : modalType === 'review' ? (editingItem.id ? 'Edit Review' : 'Add Review') : modalType === 'category' ? (editingItem.id ? 'Edit Category' : 'Add Category') : 'Edit'}
              </h3>
              <button onClick={() => { setShowModal(false); setEditingItem(null); }} className="p-2 hover:bg-[#F5EFE9] rounded-lg"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              {modalType === 'siteContent' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-[#1E1622] mb-2">Value</label>
                    {editingItem.type === 'text' ? (
                      <textarea value={editingItem.value} onChange={(e) => setEditingItem({ ...editingItem, value: e.target.value })} rows={3} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" />
                    ) : (
                      <input type={editingItem.type === 'color' ? 'color' : 'text'} value={editingItem.value} onChange={(e) => setEditingItem({ ...editingItem, value: e.target.value })} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" />
                    )}
                  </div>
                  {editingItem.type === 'image' && (
                    <div>
                      <label className="block text-sm font-medium text-[#1E1622] mb-2">Upload a new image</label>
                      <input type="file" accept="image/*" data-testid="site-content-image-upload" onChange={async (e) => {
                        const f = e.target.files[0]; if (!f) return;
                        if (f.size > 10 * 1024 * 1024) { toast.error('Please choose an image under 10 MB'); return; }
                        try { const d = await uploadFile(f, 'image'); setEditingItem((p) => ({ ...p, value: d.url })); toast.success('Image uploaded. Click Save to publish it'); }
                        catch { toast.error('Upload failed'); }
                      }} className="w-full text-sm" />
                      <p className="text-xs text-[#1E1622]/50 mt-1">JPG, PNG or WebP, up to 10 MB. Or paste an image link above.</p>
                      <div className="mt-3"><ImageLibraryPicker token={token} current={editingItem.value} onSelect={(url) => setEditingItem((p) => ({ ...p, value: url }))} /></div>
                    </div>
                  )}
                  {editingItem.type === 'image' && editingItem.value && <img src={assetUrl(editingItem.value)} alt="Preview" data-testid="site-content-image-preview" className="w-full h-40 object-cover rounded-xl" />}
                </>
              )}
              {modalType === 'category' && (
                <>
                  <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Name *</label><input type="text" value={editingItem.name} onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })} data-testid="category-name-input" placeholder="e.g. Italy" className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                  <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Description</label><textarea value={editingItem.description} onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })} rows={3} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                </>
              )}
              {modalType === 'product' && (
                <>
                  {editingItem.isNew && (
                    <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Product ID (slug)</label><input type="text" value={editingItem.id} onChange={(e) => setEditingItem({ ...editingItem, id: e.target.value })} placeholder="e.g. italy-visa-checklist" className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                  )}
                  <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Title</label><input type="text" value={editingItem.title} onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                  <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Description</label><textarea value={editingItem.description} onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })} rows={3} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Price</label><input type="number" value={editingItem.price} onChange={(e) => setEditingItem({ ...editingItem, price: parseFloat(e.target.value) })} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                    <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Category</label><select value={editingItem.category} onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value })} data-testid="product-category-select" className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none bg-white">{categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}{editingItem.category && !categories.some((c) => c.name === editingItem.category) && <option value={editingItem.category}>{editingItem.category}</option>}</select></div>
                  </div>
                  <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Cover Image</label>
                    {editingItem.image_url && <img src={editingItem.image_url.startsWith('http') ? editingItem.image_url : `${process.env.REACT_APP_BACKEND_URL}${editingItem.image_url}`} alt="cover" className="w-24 h-24 object-cover rounded-lg mb-2 border border-[#E6DAC5]" />}
                    <input type="file" accept="image/*" data-testid="product-cover-input" onChange={async (e) => { const f = e.target.files[0]; if (!f) return; try { const d = await uploadFile(f, 'image'); setEditingItem((p) => ({ ...p, image_url: d.url })); toast.success('Cover uploaded'); } catch { toast.error('Upload failed'); } }} className="w-full text-sm" />
                    <div className="mt-3"><ImageLibraryPicker token={token} current={editingItem.image_url} onSelect={(url) => setEditingItem((p) => ({ ...p, image_url: url }))} /></div>
                  </div>
                  <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Guide File (PDF emailed to buyers)</label>
                    {editingItem.guide_filename && <p className="text-xs text-[#1E1622]/60 mb-2">Current: {editingItem.guide_filename}</p>}
                    <input type="file" accept=".pdf,application/pdf" data-testid="product-guide-input" onChange={async (e) => { const f = e.target.files[0]; if (!f) return; try { const d = await uploadFile(f, 'guide'); setEditingItem((p) => ({ ...p, guide_path: d.path, guide_filename: d.filename })); toast.success('Guide uploaded'); } catch { toast.error('Upload failed'); } }} className="w-full text-sm" />
                  </div>
                  <div className="flex items-center gap-2"><input type="checkbox" id="featured" checked={editingItem.featured} onChange={(e) => setEditingItem({ ...editingItem, featured: e.target.checked })} /><label htmlFor="featured" className="text-sm">Featured</label></div>
                </>
              )}
              {modalType === 'blog' && (
                <>
                  <div><label className="block text-sm font-medium text-[#1E1622] mb-2">ID (slug)</label><input type="text" value={editingItem.id} onChange={(e) => setEditingItem({ ...editingItem, id: e.target.value })} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                  <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Title</label><input type="text" value={editingItem.title} onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                  <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Excerpt</label><textarea value={editingItem.excerpt} onChange={(e) => setEditingItem({ ...editingItem, excerpt: e.target.value })} rows={2} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Category</label><input type="text" value={editingItem.category} onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value })} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                    <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Status</label><select value={editingItem.status} onChange={(e) => setEditingItem({ ...editingItem, status: e.target.value })} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none"><option value="draft">Draft</option><option value="published">Published</option><option value="scheduled">Scheduled</option></select></div>
                  </div>
                  {editingItem.status === 'scheduled' && (
                    <div data-testid="blog-schedule-field">
                      <label className="block text-sm font-medium text-[#1E1622] mb-2">Publish on (Brisbane time) *</label>
                      <input type="datetime-local" value={editingItem.publish_at || ''} onChange={(e) => setEditingItem({ ...editingItem, publish_at: e.target.value })} data-testid="blog-publish-at" className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" />
                      <p className="text-xs text-[#1E1622]/50 mt-1">The post appears on the blog automatically at this time.</p>
                    </div>
                  )}
                  <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Image URL</label><input type="text" value={editingItem.image} onChange={(e) => setEditingItem({ ...editingItem, image: e.target.value })} data-testid="blog-image-input" className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" />
                    {editingItem.image && <img src={assetUrl(editingItem.image)} alt="Blog" className="w-full h-32 object-cover rounded-xl mt-2" />}
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <input type="file" accept="image/*" data-testid="blog-image-upload" onChange={async (e) => { const f = e.target.files[0]; if (!f) return; try { const d = await uploadFile(f, 'image'); setEditingItem((p) => ({ ...p, image: d.url })); toast.success('Image uploaded'); } catch { toast.error('Upload failed'); } }} className="text-sm" />
                      <ImageLibraryPicker token={token} current={editingItem.image} onSelect={(url) => setEditingItem((p) => ({ ...p, image: url }))} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1E1622] mb-2">Article</label>
                    <RichTextEditor value={editingItem.content || ''} onChange={(html) => setEditingItem((p) => ({ ...p, content: html }))}
                      uploadImage={async (f) => { const d = await uploadFile(f, 'image'); return assetUrl(d.url); }} />
                    <p className="text-xs text-[#1E1622]/50 mt-1">Use Heading for main sections and Subheading inside them. Add links to your guides, e.g. /shop or /destinations/italy.</p>
                  </div>
                </>
              )}
              {modalType === 'campaign' && (
                <>
                  <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Campaign Name</label><input type="text" value={editingItem.name} onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Type</label><select value={editingItem.type} onChange={(e) => setEditingItem({ ...editingItem, type: e.target.value })} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none"><option value="email">Email</option><option value="social">Social</option><option value="ads">Ads</option></select></div>
                    <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Status</label><select value={editingItem.status} onChange={(e) => setEditingItem({ ...editingItem, status: e.target.value })} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none"><option value="draft">Draft</option><option value="scheduled">Scheduled</option><option value="sent">Sent</option></select></div>
                  </div>
                  <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Subject</label><input type="text" value={editingItem.subject || ''} onChange={(e) => setEditingItem({ ...editingItem, subject: e.target.value })} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                  <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Content</label><textarea value={editingItem.content || ''} onChange={(e) => setEditingItem({ ...editingItem, content: e.target.value })} rows={4} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                </>
              )}
              {modalType === 'crm' && (
                <>
                  <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Email *</label><input type="email" value={editingItem.email} onChange={(e) => setEditingItem({ ...editingItem, email: e.target.value })} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                  <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Name</label><input type="text" value={editingItem.name || ''} onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Phone</label><input type="text" value={editingItem.phone || ''} onChange={(e) => setEditingItem({ ...editingItem, phone: e.target.value })} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                    <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Status</label><select value={editingItem.status} onChange={(e) => setEditingItem({ ...editingItem, status: e.target.value })} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none"><option value="lead">Lead</option><option value="prospect">Prospect</option><option value="customer">Customer</option></select></div>
                  </div>
                  <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Notes</label><textarea value={editingItem.notes || ''} onChange={(e) => setEditingItem({ ...editingItem, notes: e.target.value })} rows={3} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                </>
              )}
              {modalType === 'image' && (
                <>
                  <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Name</label><input type="text" value={editingItem.name} onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                  <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Image URL</label><input type="text" value={editingItem.url} onChange={(e) => setEditingItem({ ...editingItem, url: e.target.value })} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                  <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Alt Text</label><input type="text" value={editingItem.alt_text || ''} onChange={(e) => setEditingItem({ ...editingItem, alt_text: e.target.value })} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                  {editingItem.url && <img src={editingItem.url} alt="Preview" className="w-full h-40 object-cover rounded-xl" />}
                </>
              )}
              {modalType === 'bundle' && (
                <>
                  <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Bundle Name</label><input type="text" value={editingItem.name} onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                  <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Description</label><textarea value={editingItem.description} onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })} rows={2} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Price (AUD)</label><input type="number" value={editingItem.price} onChange={(e) => setEditingItem({ ...editingItem, price: parseFloat(e.target.value) })} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                    <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Discount %</label><input type="number" value={editingItem.discount} onChange={(e) => setEditingItem({ ...editingItem, discount: parseFloat(e.target.value) })} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                  </div>
                </>
              )}
              {modalType === 'knowledgeBase' && (
                <>
                  <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Title</label><input type="text" value={editingItem.title} onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                  <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Category</label><input type="text" value={editingItem.category} onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value })} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                  <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Content</label><textarea value={editingItem.content} onChange={(e) => setEditingItem({ ...editingItem, content: e.target.value })} rows={6} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                </>
              )}
              {modalType === 'review' && (
                <>
                  <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Quote *</label><textarea value={editingItem.quote} onChange={(e) => setEditingItem({ ...editingItem, quote: e.target.value })} rows={4} data-testid="review-quote-input" className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Author *</label><input type="text" value={editingItem.author} onChange={(e) => setEditingItem({ ...editingItem, author: e.target.value })} data-testid="review-author-input" className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                    <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Location</label><input type="text" value={editingItem.location} onChange={(e) => setEditingItem({ ...editingItem, location: e.target.value })} placeholder="Sydney → Florence" className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Rating</label><select value={editingItem.rating} onChange={(e) => setEditingItem({ ...editingItem, rating: parseInt(e.target.value) })} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none"><option value={5}>5 Stars</option><option value={4}>4 Stars</option><option value={3}>3 Stars</option></select></div>
                    <div><label className="block text-sm font-medium text-[#1E1622] mb-2">Status</label><select value={editingItem.status} onChange={(e) => setEditingItem({ ...editingItem, status: e.target.value })} className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none"><option value="published">Published</option><option value="draft">Draft</option></select></div>
                  </div>
                </>
              )}
            </div>
            <div className="p-6 border-t border-[#E6DAC5] flex items-center justify-end gap-4">
              <button onClick={() => { setShowModal(false); setEditingItem(null); }} className="px-6 py-2 text-[#1E1622] hover:bg-[#F5EFE9] rounded-xl">Cancel</button>
              <button onClick={handleSave} data-testid="modal-save-btn" className="btn-primary flex items-center gap-2"><Save size={18} /> Save</button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
